import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Building, Plus, Search, Filter, Grid, List, Download, RefreshCw,
  Users, DollarSign, TrendingUp, Calendar, CheckCircle, AlertTriangle,
  Clock, X, Building2, Factory, Heart, Truck, Zap, Package, Briefcase,
  Globe, ChevronRight, BarChart3, PieChart, Eye, Edit2, Trash2
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import { Progress } from './ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industry configurations
const INDUSTRY_CONFIG = {
  'Construction': { icon: Building2, color: 'from-orange-500 to-amber-500', bg: 'bg-orange-100', text: 'text-orange-600' },
  'Manufacturing': { icon: Factory, color: 'from-blue-500 to-indigo-500', bg: 'bg-blue-100', text: 'text-blue-600' },
  'Healthcare': { icon: Heart, color: 'from-red-500 to-pink-500', bg: 'bg-red-100', text: 'text-red-600' },
  'Logistics': { icon: Truck, color: 'from-purple-500 to-indigo-500', bg: 'bg-purple-100', text: 'text-purple-600' },
  'Energy': { icon: Zap, color: 'from-yellow-500 to-orange-500', bg: 'bg-yellow-100', text: 'text-yellow-600' },
  'Technology': { icon: Globe, color: 'from-cyan-500 to-blue-500', bg: 'bg-cyan-100', text: 'text-cyan-600' },
  'Retail': { icon: Package, color: 'from-green-500 to-emerald-500', bg: 'bg-green-100', text: 'text-green-600' },
  'General Industry': { icon: Briefcase, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-600' },
  'Other': { icon: Building, color: 'from-gray-500 to-slate-500', bg: 'bg-gray-100', text: 'text-gray-600' }
};

const SUBSCRIPTION_STATUS_CONFIG = {
  'active': { label: 'Active', color: 'bg-green-100 text-green-700', dotColor: 'bg-green-500' },
  'trialing': { label: 'Trial', color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
  'past_due': { label: 'Past Due', color: 'bg-yellow-100 text-yellow-700', dotColor: 'bg-yellow-500' },
  'canceled': { label: 'Canceled', color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' },
  'unpaid': { label: 'Unpaid', color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' },
  'free': { label: 'Free', color: 'bg-gray-100 text-gray-700', dotColor: 'bg-gray-500' }
};

const INDUSTRIES = [
  'Construction', 'Manufacturing', 'Healthcare', 'Logistics', 
  'Energy', 'Technology', 'Retail', 'General Industry', 'Other'
];

const CompanyManagementPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    industry: '',
    subscription_status: '',
    company_size: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: 'Other',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    employee_count: 0,
    phone: '',
    email: '',
    website: '',
    notes: ''
  });

  const getToken = () => localStorage.getItem('token');

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (filters.industry) params.append('industry', filters.industry);
      if (filters.subscription_status) params.append('subscription_status', filters.subscription_status);
      if (filters.company_size) params.append('company_size', filters.company_size);
      if (filters.is_active) params.append('is_active', filters.is_active);

      const response = await axios.get(`${API}/company-management/companies?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      setCompanies(response.data.companies || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load companies",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, sortBy, sortOrder, searchQuery, filters, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-management/dashboard/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchStats();
  }, [fetchCompanies, fetchStats]);

  const handleCreateCompany = async () => {
    try {
      await axios.post(`${API}/company-management/companies`, newCompany, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast({ title: "Success", description: "Company created successfully" });
      setShowCreateDialog(false);
      setNewCompany({
        name: '',
        industry: 'Other',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        employee_count: 0,
        phone: '',
        email: '',
        website: '',
        notes: ''
      });
      fetchCompanies();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create company",
        variant: "destructive"
      });
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const response = await axios.get(`${API}/company-management/export?format=${format}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (format === 'csv' && response.data.csv_data) {
        const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast({ title: "Success", description: `Exported ${response.data.total} companies` });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to export companies",
        variant: "destructive"
      });
    }
  };

  const getIndustryConfig = (industry) => {
    return INDUSTRY_CONFIG[industry] || INDUSTRY_CONFIG['Other'];
  };

  const getSubscriptionConfig = (status) => {
    return SUBSCRIPTION_STATUS_CONFIG[status] || SUBSCRIPTION_STATUS_CONFIG['free'];
  };

  const clearFilters = () => {
    setFilters({
      industry: '',
      subscription_status: '',
      company_size: '',
      is_active: ''
    });
    setSearchQuery('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Company Management"
        subtitle="Manage client companies, subscriptions, and analytics"
        icon={Building}
        variant="indigo"
        onRefresh={() => { fetchCompanies(); fetchStats(); }}
        loading={loading}
        actions={
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleExport('csv')}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Company
            </Button>
          </div>
        }
        stats={stats ? [
          { icon: Building, label: 'Total', value: stats.total_companies, color: 'blue' },
          { icon: CheckCircle, label: 'Active', value: stats.active_companies, color: 'green' },
          { icon: Clock, label: 'Recent', value: stats.recent_signups, color: 'purple' },
          { icon: DollarSign, label: 'Revenue', value: `$${(stats.total_revenue || 0).toLocaleString()}`, color: 'emerald' }
        ] : []}
      />

      {/* Dashboard Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* By Subscription Status */}
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">By Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats.by_subscription || {}).map(([status, count]) => {
                  const config = getSubscriptionConfig(status);
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${config.dotColor}`}></div>
                        <span className="text-sm">{config.label}</span>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* By Industry */}
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">By Industry</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {Object.entries(stats.by_industry || {}).slice(0, 5).map(([industry, count]) => {
                  const config = getIndustryConfig(industry);
                  return (
                    <div key={industry} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <config.icon className={`w-4 h-4 ${config.text}`} />
                        <span className="text-sm truncate max-w-24">{industry}</span>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* By Size */}
          <Card className="rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">By Size</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats.by_size || {}).map(([size, count]) => (
                  <div key={size} className="flex items-center justify-between">
                    <span className="text-sm capitalize">{size}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-indigo-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold mt-1">${(stats.total_revenue || 0).toLocaleString()}</p>
                <p className="text-indigo-200 text-xs mt-2">
                  {stats.active_companies} paying customers
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & Search */}
      <Card className="rounded-2xl">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input 
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="pl-9 rounded-xl"
              />
            </div>

            {/* Industry Filter */}
            <Select 
              value={filters.industry || 'all'} 
              onValueChange={(v) => {
                setFilters(prev => ({ ...prev, industry: v === 'all' ? '' : v }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {INDUSTRIES.map(ind => (
                  <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Subscription Filter */}
            <Select 
              value={filters.subscription_status || 'all'} 
              onValueChange={(v) => {
                setFilters(prev => ({ ...prev, subscription_status: v === 'all' ? '' : v }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder="Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trialing">Trial</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
                <SelectItem value="free">Free</SelectItem>
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select 
              value={sortBy} 
              onValueChange={(v) => setSortBy(v)}
            >
              <SelectTrigger className="w-40 rounded-xl">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Date Created</SelectItem>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="employee_count">Employees</SelectItem>
                <SelectItem value="health_score">Health Score</SelectItem>
              </SelectContent>
            </Select>

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-lg ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Clear Filters */}
            {(searchQuery || filters.industry || filters.subscription_status) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Companies Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companies.map((company) => {
            const industryConfig = getIndustryConfig(company.industry);
            const IndustryIcon = industryConfig.icon;
            const subscriptionConfig = getSubscriptionConfig(company.billing?.subscription_status);
            
            return (
              <Card 
                key={company.id} 
                className="rounded-2xl hover:shadow-xl transition-all cursor-pointer group overflow-hidden"
                onClick={() => navigate(`/admin/companies/${company.id}`)}
                data-testid={`company-card-${company.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${industryConfig.color}`}></div>
                
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${industryConfig.color} flex items-center justify-center shadow-lg`}>
                        <IndustryIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg group-hover:text-blue-600 transition-colors">
                          {company.name}
                        </CardTitle>
                        <Badge className={`${industryConfig.bg} ${industryConfig.text} mt-1`}>
                          {company.industry}
                        </Badge>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {/* Health Score */}
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-500">Health Score</span>
                        <span className={`font-semibold ${
                          company.health_score >= 70 ? 'text-green-600' : 
                          company.health_score >= 40 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{company.health_score}/100</span>
                      </div>
                      <Progress value={company.health_score} className="h-1.5" />
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        {company.employee_count || 0}
                      </div>
                      <Badge className={subscriptionConfig.color}>
                        {subscriptionConfig.label}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {company.tenure_days}d
                      </div>
                    </div>

                    {/* Revenue */}
                    {company.billing?.total_paid > 0 && (
                      <div className="flex items-center justify-between text-sm pt-2 border-t">
                        <span className="text-gray-500">Total Revenue</span>
                        <span className="font-semibold text-green-600">
                          ${(company.billing.total_paid || 0).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List View */
        <Card className="rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Industry</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Subscription</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Employees</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Health</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Revenue</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Tenure</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map((company) => {
                  const industryConfig = getIndustryConfig(company.industry);
                  const IndustryIcon = industryConfig.icon;
                  const subscriptionConfig = getSubscriptionConfig(company.billing?.subscription_status);
                  
                  return (
                    <tr 
                      key={company.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/admin/companies/${company.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${industryConfig.color} flex items-center justify-center`}>
                            <IndustryIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{company.name}</p>
                            <p className="text-xs text-gray-500">{company.city}, {company.state}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${industryConfig.bg} ${industryConfig.text}`}>
                          {company.industry}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={subscriptionConfig.color}>
                          {subscriptionConfig.label}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {company.employee_count || 0}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Progress value={company.health_score} className="w-16 h-2" />
                          <span className="text-sm font-medium">{company.health_score}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-green-600">
                        ${(company.billing?.total_paid || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {company.tenure_days} days
                      </td>
                      <td className="px-6 py-4">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((pagination.page - 1) * pagination.page_size) + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total} companies
          </p>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button 
              variant="outline" 
              size="sm"
              disabled={pagination.page === pagination.total_pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && companies.length === 0 && (
        <Card className="rounded-2xl">
          <CardContent className="text-center py-16">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No companies found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filters.industry || filters.subscription_status 
                ? 'Try adjusting your filters' 
                : 'Add your first company to get started'}
            </p>
            {!(searchQuery || filters.industry || filters.subscription_status) && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Company
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              Add New Company
            </DialogTitle>
            <DialogDescription>
              Create a new company profile for management and tracking
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company Name *</Label>
                <Input 
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <Label>Industry *</Label>
                <Select 
                  value={newCompany.industry} 
                  onValueChange={(v) => setNewCompany({...newCompany, industry: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input 
                value={newCompany.address}
                onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                placeholder="123 Main Street"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input 
                  value={newCompany.city}
                  onChange={(e) => setNewCompany({...newCompany, city: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={newCompany.state}
                  onChange={(e) => setNewCompany({...newCompany, state: e.target.value})}
                />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <Input 
                  value={newCompany.zip_code}
                  onChange={(e) => setNewCompany({...newCompany, zip_code: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee Count</Label>
                <Input 
                  type="number"
                  value={newCompany.employee_count}
                  onChange={(e) => setNewCompany({...newCompany, employee_count: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input 
                  value={newCompany.website}
                  onChange={(e) => setNewCompany({...newCompany, website: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newCompany.phone}
                  onChange={(e) => setNewCompany({...newCompany, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newCompany.email}
                  onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                  placeholder="contact@company.com"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea 
                value={newCompany.notes}
                onChange={(e) => setNewCompany({...newCompany, notes: e.target.value})}
                placeholder="Additional information..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCompany} disabled={!newCompany.name}>
              <Plus className="w-4 h-4 mr-2" /> Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyManagementPage;
